import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthenticationModule } from './authentication/authentication.module';
import { CommonModule, DecimalPipe, registerLocaleData } from '@angular/common';
import { LayoutModule } from './layout/layout.module';
import { HomeModule } from './home/home.module';
import { BrowserModule } from '@angular/platform-browser';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Utils } from './classes/utils';
import { DialogModule } from './common/dialogs/dialog.module';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpRequestInterceptor } from './common/global-spinner/HttpRequestInterceptor';
import { HttpErrorInterceptor } from './common/dialogs/error.interceptor';
import { environment } from '../environments/environment';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { RouteReuseStrategy } from '@angular/router';
import { RouteReuseService } from './services/route-reuse.service';
import localeiIt from '@angular/common/locales/it';

registerLocaleData(localeiIt);

/**
 * Defence-in-depth: strip leftover auth callback fragments from the URL before Keycloak init.
 * keycloak-js 26 now handles the `iss` param (RFC 9207) natively, but if any fragment
 * residue remains from a failed cycle or browser back-navigation we clean it here.
 */
function cleanupAuthFragment(): void {
    const hash = window.location.hash;
    if (hash && /[#&](iss|code|state|session_state)=/.test(hash)) {
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.replaceState(null, '', cleanUrl);
    }
}

function initializeKeycloak(keycloak: KeycloakService) {
    return () => {
        cleanupAuthFragment();
        return keycloak.init({
        config: {
            url: environment.keycloakUrl,
            realm: 'gpporte',
            clientId: 'spa-gp-frontend',
        },
        initOptions: {
            onLoad: 'check-sso',
            pkceMethod: 'S256',
            redirectUri: `${window.location.origin}/`,
            silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
            checkLoginIframe: false,
            enableLogging: !environment.production,
        },
        }).catch((err) => {
            console.error('[KC] init failed:', err ?? 'unknown error (undefined)');
            throw err;
        });
    };
}


@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [

        CommonModule,
        BrowserAnimationsModule,
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        MatProgressSpinnerModule,

        LayoutModule,
        MatDialogModule,
        AuthenticationModule,
        DialogModule,
        KeycloakAngularModule,
        HttpClientModule,
        TranslateModule.forRoot({
            defaultLanguage: 'it',
            loader: {
                provide: TranslateLoader,
                useFactory: (http: HttpClient) => new TranslateHttpLoader(http),
                deps: [HttpClient]
            }
        }),
        HomeModule,
    ],
    providers: [
        DecimalPipe,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeKeycloak,
            multi: true,
            deps: [KeycloakService],
        },
        {
            provide: LOCALE_ID, useValue: 'it-IT'
        },
        {
            provide: Utils,
            useFactory: (dialog, keycloak) => {
                const service = new Utils();
                service.injectDialogService(dialog);
                service.injectKeycloackService(keycloak);
                return service;
            },
            deps: [MatDialog, KeycloakService]
        },

        { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
        { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        { provide: RouteReuseStrategy, useClass: RouteReuseService }
    ],
    bootstrap: [AppComponent],
})
export class AppModule {
}
